import type {
  PublishGlobalOnlyChange,
  PublishNodeDisclosure,
  PublishNodeField,
} from "@repo/app-types/helpers/playbookNodePublish";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import type { FC } from "react";

const fieldLabels: Record<PublishNodeField, string> = {
  name: "Name",
  description: "Description",
  grading_context: "Grading context",
  grading_value_type: "Grading type",
  coaching_priority: "Coaching priority",
};

const globalOnlyLabels: Record<PublishGlobalOnlyChange, string> = {
  moves: "Moves",
  reorders: "Reordering",
  deletions: "Deletions",
};

/** "Moves" / "Moves and reordering" / "Moves, reordering and deletions". */
function joinNaturally(labels: readonly string[]): string {
  const cased = labels.map((label, index) =>
    index === 0 ? label : label.toLowerCase(),
  );
  return cased.length <= 1
    ? cased.join("")
    : `${cased.slice(0, -1).join(", ")} and ${cased.at(-1)}`;
}

export interface PublishNodeConfirmationModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Names the node kind in the copy. */
  nodeLabel: "criteria" | "section";
  disclosure: PublishNodeDisclosure;
  onConfirm: () => void;
}

/**
 * States what an inline publish will carry and what stays behind for the
 * playbook's main Publish button. The caller shows it only when there is
 * something to disclose: a new-section dependency, or pending draft changes
 * (moves, reorders, deletions) a node publish never carries.
 */
export const PublishNodeConfirmationModal: FC<
  PublishNodeConfirmationModalProps
> = (props) => {
  const { isOpen, onOpenChange, nodeLabel, disclosure, onConfirm } = props;
  const { isNew, changedFields, newAncestorSectionCount, globalOnlyChanges } =
    disclosure;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={`Publish this ${nodeLabel}`}
      confirmText="Publish"
      variant={AlertDialogVariant.Confirm}
      onConfirm={onConfirm}
    >
      <div className="space-y-3">
        {isNew ? (
          <p>
            Publishing this {nodeLabel} adds it to the live playbook
            {newAncestorSectionCount === 0
              ? ""
              : `, along with the new ${
                  newAncestorSectionCount === 1 ? "section" : "sections"
                } it belongs to (added at the bottom of the playbook)`}
            .
          </p>
        ) : changedFields.length > 0 ? (
          <>
            <p>
              Publishing this {nodeLabel} will only publish the edits you made
              to:
            </p>
            <ul className="list-disc pl-5">
              {changedFields.map((field) => (
                <li key={field}>{fieldLabels[field]}</li>
              ))}
            </ul>
          </>
        ) : (
          <p>
            Publishing this {nodeLabel} will only publish the edits made to it.
          </p>
        )}
        {globalOnlyChanges.length > 0 ? (
          <p>
            {joinNaturally(
              globalOnlyChanges.map((kind) => globalOnlyLabels[kind]),
            )}
            {" can only be published with the playbook’s main Publish button."}
          </p>
        ) : null}
      </div>
    </ConfirmationModal>
  );
};

export interface PublishSettingConfirmationModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  settingLabel: "scope" | "outcome" | "summary template";
  globalOnlyChanges: readonly PublishGlobalOnlyChange[];
  onConfirm: () => void;
}

/** Confirm that a setting-only publish leaves structural tree edits pending. */
export const PublishSettingConfirmationModal: FC<
  PublishSettingConfirmationModalProps
> = (props) => {
  const { isOpen, onOpenChange, settingLabel, globalOnlyChanges, onConfirm } =
    props;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={`Publish this ${settingLabel}`}
      confirmText="Publish"
      variant={AlertDialogVariant.Confirm}
      onConfirm={onConfirm}
    >
      <div className="space-y-3">
        <p>
          Publishing this {settingLabel} will only publish the edits made to it.
        </p>
        {globalOnlyChanges.length > 0 ? (
          <p>
            {joinNaturally(
              globalOnlyChanges.map((kind) => globalOnlyLabels[kind]),
            )}
            {" can only be published with the playbook’s main Publish button."}
          </p>
        ) : null}
      </div>
    </ConfirmationModal>
  );
};
